import type { SnRouteConsolidationRouteStopOutDTO } from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { combineStops, intersectTimeslots, LocationContactConflict, MergedRoute, stopName, stopStartsAt } from '@app/pages/expedition/create/mergeRules.ts';
import { validateRouteAssignments } from '@app/pages/expedition/create/routeRules.ts';
import type { MergeRouteBuilder } from '@app/pages/expedition/create/mappers/mergeToForm.ts';

/**
 * The route of a shipment built from a route-optimization group.
 *
 * The manual merge decides the stop order itself (pick-ups first, by date). Here the order is already
 * decided: the backend turned the AI route into `routeStops`, one entry per stop of the shipment, and
 * this keeps that order exactly — stops are folded only where the backend put several notifications
 * on the same entry, never moved.
 */

/** A notification's stops after the ordinary prefill: loading stops carry its orders, unloading stops their ids. */
type SnStops = Pick<NewExpeditionForm, 'locations'>;

const hasOrders = (loc: ExpeditionLocality) => (loc.orders?.length ?? 0) > 0;
const hasUnloading = (loc: ExpeditionLocality) => (loc.unloadingOrderIds?.length ?? 0) > 0;

/**
 * The notification's own stop for this route stop. Matched by address-book id first; a notification
 * has one loading and one unloading place, so the role alone finds it when the id does not match —
 * a superseded location is prefilled with its replacement's id, not the one the backend routed.
 */
const templateFor = (source: SnStops, partnerLocationId: string, role: 'load' | 'unload'): ExpeditionLocality | undefined => {
  const candidates = (source?.locations ?? []).filter(role === 'load' ? hasOrders : hasUnloading);
  return candidates.find(loc => loc.companyLocationId === partnerLocationId) ?? (role === 'load' ? candidates[0] : candidates[candidates.length - 1]);
};

const issueCount = (locations: ExpeditionLocality[]) => validateRouteAssignments({ locations } as NewExpeditionForm).length;

/**
 * A stop that unloads one notification and loads another stays one stop when the route rules accept
 * it, which they do as long as the unloaded orders were loaded earlier. Should they not, the stop is
 * split into two at the same address, unloading first, so the route starts out valid.
 */
const splitIfRejected = (locations: ExpeditionLocality[]): ExpeditionLocality[] => {
  let route = locations;
  for (let index = 0; index < route.length; index++) {
    const loc = route[index];
    if (!hasOrders(loc) || !hasUnloading(loc)) continue;

    const split = [...route.slice(0, index), { ...loc, orders: null }, { ...loc, id: `${loc.id}-load`, unloadingOrderIds: [] }, ...route.slice(index + 1)];
    if (issueCount(split) < issueCount(route)) {
      route = split;
      index++;
    }
  }
  return route;
};

export const routeFromConsolidation = (routeStops: SnRouteConsolidationRouteStopOutDTO[], sourcesBySnId: Map<string, SnStops>): MergedRoute => {
  const contactConflicts: LocationContactConflict[] = [];
  const timeslotConflicts: MergedRoute['timeslotConflicts'] = [];

  const folded = [...routeStops]
    .sort((a, b) => a.sequence - b.sequence)
    .map(routeStop => {
      const templates = [
        ...(routeStop.loadSupplierNotificationIds ?? []).map(id => templateFor(sourcesBySnId.get(id), routeStop.partnerLocationId, 'load')),
        ...(routeStop.unloadSupplierNotificationIds ?? []).map(id => {
          const template = templateFor(sourcesBySnId.get(id), routeStop.partnerLocationId, 'unload');
          // Only the unloading belongs here; the template is the same place for the other notification too.
          return template ? { ...template, orders: null } : undefined;
        }),
      ].filter(Boolean);
      if (templates.length === 0) return null;

      return templates.slice(1).reduce((target, source) => {
        const shared = intersectTimeslots(target.timeslots, source.timeslots);
        if (shared === null) timeslotConflicts.push({ locationName: stopName(target) || routeStop.locationName || '' });
        return combineStops(target, source, shared ?? target.timeslots, contactConflicts);
      }, templates[0]);
    })
    .filter(Boolean)
    .map((loc, index) => ({
      ...loc,
      orders: hasOrders(loc) ? loc.orders : null,
      // The form's convention: the first stop has no unloading list, every later one at least an empty one.
      unloadingOrderIds: index === 0 ? null : loc.unloadingOrderIds ?? [],
    }));

  return { locations: splitIfRejected(folded), keptApart: [], contactConflicts, timeslotConflicts };
};

/**
 * {@link routeFromConsolidation} as the merge's route builder. A notification's detail carries the
 * notification's own id, which is what the route stops name.
 */
export const consolidationRouteBuilder =
  (routeStops: SnRouteConsolidationRouteStopOutDTO[]): MergeRouteBuilder =>
  (sources, _values, details) =>
    routeFromConsolidation(routeStops, new Map(details.map((detail, index) => [detail.id, sources[index]])));

/**
 * Stops wanted earlier than a stop before them on the route — the AI orders stops by loading date only,
 * so the hours can come out backwards. Compared with the nearest earlier stop that has a date at all.
 */
export const stopsStartingBeforePrevious = (locations: ExpeditionLocality[]): Set<number> => {
  const flagged = new Set<number>();
  let previous: number | null = null;

  (locations ?? []).forEach((loc, index) => {
    const startsAt = stopStartsAt(loc);
    if (startsAt === null) return;
    if (previous !== null && startsAt < previous) flagged.add(index);
    previous = startsAt;
  });

  return flagged;
};
